import { IconButton } from "@mui/material";
import {
  Close,
  CheckmarkCircleOutline,
  ShieldCheckmarkOutline,
} from "@raresail/react-ionicons";
import { useNavigate } from "react-router-dom";
import { ACCOUNT } from "../../../constants/constants";
import useForm from "../../../hooks/common/useForm";
import useSet from "../../../hooks/common/useSet";
import { useTitle } from "../../../hooks/common/useTitle";
import { useOnboardForm } from "../../../hooks/onboard/useOnboardForm";
import { RenderForm } from "../../../utils/formUtils";
import { useAuth } from "../../context/AuthContext";
import { StepperView, useStepper } from "../../stepper/StepperView";

/**
 * Verified-locked summary shown to users whose admin has approved
 * their KYC. We don't render the multi-step form in this state — the
 * data is final and can only be changed by an admin via the admin
 * panel. (Server-side, UserSerializer.update() enforces the same lock.)
 */
const VerifiedSummary = ({ user, onClose }: any) => {
  const fields: Array<[string, string]> = [
    ["Full name", `${user?.first_name || ""} ${user?.last_name || ""}`.trim() || "—"],
    ["Phone", user?.phone_number || "—"],
    ["Email", user?.email || "—"],
    ["ID type", (user?.id_type || "").replace(/_/g, " ") || "—"],
    ["ID number", user?.nin || "—"],
    [
      "Date of birth",
      user?.dateofbirth
        ? new Date(user.dateofbirth).toLocaleDateString()
        : "—",
    ],
  ];
  return (
    <div
      className="p20 r10 whiteBg flexColumn gap15 relative"
      style={{ border: "1px solid #bbf7d0" }}
    >
      <div className="absolute" style={{ top: 8, right: 8 }}>
        <IconButton size="small" onClick={onClose}>
          <Close fontSize={18} />
        </IconButton>
      </div>
      <div className="flexNullCenter gap10">
        <ShieldCheckmarkOutline color="#15803d" fontSize={28} />
        <div className="flexColumn gap5">
          <div className="font16 semiBold" style={{ color: "#15803d" }}>
            Identity verified
          </div>
          <div className="font12 grey" style={{ lineHeight: 1.4 }}>
            Your KYC is complete. These details are locked — contact support
            if anything needs to change.
          </div>
        </div>
      </div>
      <div className="flexColumn gap10">
        {fields.map(([label, value]) => (
          <div key={label} className="flexSpaceCenter">
            <div className="font12 grey">{label}</div>
            <div className="font13 semiBold textDark">{value}</div>
          </div>
        ))}
      </div>
      <div
        className="flexNullCenter gap5 font12 mt10"
        style={{ color: "#15803d" }}
      >
        <CheckmarkCircleOutline fontSize={16} /> All features unlocked
      </div>
    </div>
  );
};

export const OnboardPage = () => {
  const navigate = useNavigate();
  useTitle(ACCOUNT);

  const { user } = useAuth();
  const { submitting, error, setError, uploadData } = useSet();
  const { form, updateFormValues, onChange, validateForm } = useForm(user);
  const { steps, onSubmit, submitText } = useOnboardForm({
    form,
    updateFormValues,
  });

  // Hooks must be called unconditionally — keep useStepper above the
  // early return so render order stays stable for verified users.
  const { current, next, prev } = useStepper();

  // Locked-verified state — admin has approved, no further edits.
  if (user?.is_verified) {
    return (
      <div className="p15">
        <VerifiedSummary user={user} onClose={() => navigate(-1)} />
      </div>
    );
  }

  const submitForm = (payload: any) => {
    const submitData = onSubmit({ payload });
    // HANDLE POST
    if (submitData != null) {
      uploadData(submitData);
    }
  };

  function onSubmitStepper(e: any) {
    e.preventDefault();
    const validationFields: any = steps[current]?.fields;
    // stepper form validation
    if (validateForm(validationFields, setError)) {
      if (current === steps.length - 1) {
        submitForm(form);
      } else {
        next();
      }
    }
  }

  return (
    <form onSubmit={onSubmitStepper}>
      <div className="p15">
        <div className="flexColumn gap20 p20 whiteBg r10 relative hidden">
          <div className="absolute" style={{ top: 5, right: 5 }}>
            <IconButton onClick={() => navigate(-1)}>
              <Close fontSize={20} />
            </IconButton>
          </div>
          <StepperView
            prev={prev}
            next={next}
            current={current}
            submitting={submitting}
            submitText={submitText}
            steps={[
              ...steps?.map((step, i) => {
                const { fields } = step || {};
                return {
                  content: (
                    <div className="flexColumn gap15">
                      {RenderForm({
                        fields,
                        form,
                        updateFormValues,
                        onChange,
                        error,
                      })}
                    </div>
                  ),
                };
              }),
            ]}
          />
        </div>
      </div>
    </form>
  );
};
